import { AnimatePresence } from 'framer-motion';
import React, { FC, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, SearchIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../components/button/index';
import { EmptySearchList } from '../../components/empty-search-list/index';
import { Input, InputStatus } from '../../components/input/index';
import { CrossfadePanel } from '../../components/motion/index';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/popover/index';
import { PopoverAnchor } from '../../components/popover/popover-anchor.util';
import {
  SnapshotHooks,
  Stakeholder,
  useDebounce,
  useModalState,
  useScreenSize,
} from '../../hooks/index';
import { selectedCompanyStorage } from '../../storage/company/selected-company.util';
import { StakeholderRole } from '../../types/stakeholder.types';
import { formatDisplayValue } from '../../utils/format-display-value.util';
import { AutomaticVesting } from './automatic-vesting/index';
import { ConsolidatedPosition } from './consolidated-position';
import { DividendsLedger } from './dividends-ledger/index';
import { EquityGraph, PositionGraph } from './graphs/index';
import { InvestmentLedger } from './investment-ledger/index';
import { PageScreen, pageScreenTitleMap } from './my-holdings.types';
import { MyHoldingsPlaceholder } from './my-holdings-loading-placeholders';
import { OwnershipSummary } from './ownership-summary';
import { ReverseVesting } from './reverse-vesting/index';
import { SafeLedger } from './safe-ledger/index';
import { StakeholderRow } from './stakeholder-row';
import { SummaryItem } from './summary-item';

const MyHoldings: FC = () => {
  const [screen, setScreen] = useState(PageScreen.MY_HOLDINGS);

  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const company = selectedCompanyStorage(({ company }) => company);
  const stakeholder = selectedCompanyStorage(({ company }) => company?.stakeholder);
  const userRoleInCompany = company?.roleInCompany || StakeholderRole.STAKEHOLDER;
  const currency = company?.currency;
  const userStakeholderId = stakeholder?.id || '';
  const isCompanyContextReady = company?.id === companyId;
  const [stakeholderId, setStakeholderId] = useState(userStakeholderId);
  const [search, setSearch] = useState('');
  const [debounceSearch, setDebounceSearch] = useState('');
  const { debounceCallback } = useDebounce({ callback: setDebounceSearch });
  const [isFocused, setFocus] = useState(false);

  const onStakeholderHoldings = screen === PageScreen.STAKEHOLDERS_HOLDINGS;

  const handleSearch = (value: string) => {
    debounceCallback(value);
    setSearch(value);
  };

  const handleStakeholderHoldingsSearch = (value: string) => {
    setStakeholderId('');
    handleSearch(value);
  };

  const {
    fetchNextPage,
    hasNextPage,
    stakeholders: unSortedStakeholders,
  } = Stakeholder.useInfiniteStakeholders({
    companyId,
    enabled: screen === PageScreen.STAKEHOLDERS_HOLDINGS,
    numberOfFetchedItems: 25,
    search: debounceSearch || '',
  });

  const stakeholders = [...unSortedStakeholders].sort((a, b) => {
    const aHasIssued = a.hasShares;
    const bHasIssued = b.hasShares;

    if (aHasIssued !== bHasIssued) {
      return aHasIssued ? -1 : 1;
    }

    return a.fullName.localeCompare(b.fullName);
  });
  const { isOpen, toggler } = useModalState();

  const selectedStakeholderName =
    stakeholders.find((item) => item.id === stakeholderId)?.fullName ?? stakeholder?.fullName ?? '';

  const holdingsQueriesEnabled = isCompanyContextReady && !!companyId && !!stakeholderId;

  const { ownershipSummary, isLoading: isLoadingOwnershipSummary } =
    Stakeholder.useOwnershipSummary({
      companyId,
      enabled: holdingsQueriesEnabled,
      stakeholderId: stakeholderId,
    });

  const { isHaveVestingTasks, isLoading: isLoadingVestingTaskQuery } =
    Stakeholder.useHaveVestingTasks({
      companyId,
      enabled: holdingsQueriesEnabled,
      stakeholderId,
    });

  const { isLoading: isLoadingInvestmentSummary, investmentSummary } =
    Stakeholder.useInvestmentSummary({
      companyId,
      enabled: holdingsQueriesEnabled,
      stakeholderId,
    });

  const { consolidatedPosition, isLoading: isLoadingConsolidatedPosition } =
    Stakeholder.useConsolidatedPosition({
      companyId,
      enabled: holdingsQueriesEnabled,
      stakeholderId,
    });

  const { snapshot } = SnapshotHooks.useCurrent({
    companyId,
    capTableMode: true,
    enabled: isCompanyContextReady && !!companyId,
  });

  const { isMobile } = useScreenSize();

  const hasVesting = isHaveVestingTasks;

  const isLoading =
    (!!companyId && !isCompanyContextReady) ||
    isLoadingVestingTaskQuery ||
    isLoadingOwnershipSummary ||
    isLoadingConsolidatedPosition ||
    isLoadingInvestmentSummary;

  const isEmptyYourParticipation = !investmentSummary?.stakeholderEstimatedValue;

  const isEmptyCompanyInvestmentSummary = !investmentSummary?.latestSharePrice;

  useEffect(() => {
    if (screen !== PageScreen.MY_HOLDINGS) return;
    setStakeholderId(userStakeholderId);
  }, [companyId, userStakeholderId, screen]);

  return (
    <div className="flex h-full w-full flex-col gap-4 overflow-x-hidden rounded-lg p-1">
      <AnimatePresence mode="wait">
        {isLoading ? (
          <CrossfadePanel
            className="h-full w-full"
            key="holdings-loading"
            panelKey="holdings-loading"
          >
            <MyHoldingsPlaceholder />
          </CrossfadePanel>
        ) : (
          <CrossfadePanel
            className="flex h-full w-full flex-col gap-4"
            key="holdings-content"
            panelKey="holdings-content"
          >
            {userRoleInCompany !== StakeholderRole.STAKEHOLDER && (
              <div className="flex gap-4 border-b-[1px] border-gray-200 px-4">
                {pageScreenTitleMap.map(([screenType, translationKey]) => (
                  <Button
                    className={twMerge(
                      'w-fit translate-y-[1px] rounded-none border-b-2 border-transparent px-1 pb-4 text-sm font-[450] text-gray-400 transition-all',
                      screen === screenType && 'border-brand-700 font-[550] text-brand-700',
                    )}
                    disabledStyle={ButtonDisableStyle.NONE}
                    key={translationKey}
                    onClick={() => {
                      if (screenType === PageScreen.MY_HOLDINGS)
                        setStakeholderId(userStakeholderId);
                      if (screenType === PageScreen.STAKEHOLDERS_HOLDINGS) setStakeholderId('');

                      setScreen(screenType);
                      handleSearch('');
                    }}
                    styleType="NONE"
                  >
                    <AppFormattedMessage id={translationKey} />
                  </Button>
                ))}
              </div>
            )}

            <div
              className={twMerge(
                'flex w-full overflow-hidden rounded-lg p-4 shadow-sm',
                !hasVesting && 'h-full',
                debounceSearch && !stakeholders.length && 'h-full',
                onStakeholderHoldings && 'gap-4',
                onStakeholderHoldings && isMobile && 'flex-col',
              )}
            >
              {isMobile ? (
                onStakeholderHoldings && (
                  <Popover onOpenChange={toggler} open={isOpen}>
                    <PopoverAnchor asChild>
                      <div className="flex h-11 w-full items-center justify-between overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-[6px] text-sm font-[450] text-gray-400">
                        <Input
                          customStatus={InputStatus.WITHOUT_BORDER}
                          iconBeforeInput={<SearchIcon iconColor="#98A2B3" />}
                          inputClassName="h-9 w-full "
                          onBlur={() => setFocus(false)}
                          onChange={handleStakeholderHoldingsSearch}
                          onFocus={() => {
                            setFocus(true);
                          }}
                          placeholder={
                            search || isFocused ? '' : <AppFormattedMessage id="SEARCH" />
                          }
                          placeholderClassName="pl-6 !text-gray-400"
                          value={search || ''}
                          wrapperClassName={twMerge(
                            'transition-all duration-500 overflow-hidden h-9 w-full',
                          )}
                        />

                        <PopoverTrigger className="flex h-full w-1/2 items-center justify-end">
                          <ChevronDownIcon />
                        </PopoverTrigger>
                      </div>
                    </PopoverAnchor>

                    {Boolean(stakeholders.length) && (
                      <PopoverContent
                        className="block max-h-[200px] w-[var(--radix-popper-anchor-width)] overflow-hidden overflow-y-auto rounded p-2 shadow-2xl"
                        id="infinite-scroll-target"
                      >
                        <InfiniteScroll
                          className="relative flex h-full max-w-full flex-col divide-y divide-gray-200 border-b-[1px] border-gray-200"
                          dataLength={stakeholders.length - 1}
                          hasMore={hasNextPage}
                          loader={
                            <div className="absolute h-full w-full animate-pulse bg-brand-300/10" />
                          }
                          next={fetchNextPage}
                          scrollableTarget="infinite-scroll-target"
                        >
                          {stakeholders.map(
                            ({ email, fullName, id }) =>
                              id !== userStakeholderId && (
                                <StakeholderRow
                                  email={email}
                                  fullName={fullName}
                                  isMobile
                                  isSelected={id === stakeholderId}
                                  key={id}
                                  onClick={() => {
                                    setStakeholderId(id);
                                    toggler(false);
                                  }}
                                />
                              ),
                          )}
                        </InfiniteScroll>
                      </PopoverContent>
                    )}
                  </Popover>
                )
              ) : (
                <div
                  className={twMerge(
                    'sticky flex min-h-full w-full shrink-0 flex-col overflow-y-auto rounded-[10px] shadow-sm transition-all duration-300',
                    screen === PageScreen.MY_HOLDINGS ? 'w-0' : 'w-[220px]',
                  )}
                  id="infinite-scroll-target"
                >
                  <div className="flex h-11 items-center gap-1 bg-gray-50 p-[6px] text-sm font-[450] text-gray-400">
                    <Input
                      customStatus={InputStatus.WITHOUT_BORDER}
                      iconBeforeInput={<SearchIcon iconColor="#98A2B3" />}
                      inputClassName="h-9"
                      onBlur={() => setFocus(false)}
                      onChange={handleStakeholderHoldingsSearch}
                      onFocus={() => {
                        setFocus(true);
                      }}
                      placeholder={search || isFocused ? '' : <AppFormattedMessage id="SEARCH" />}
                      placeholderClassName="pl-6 !text-gray-400"
                      value={search || ''}
                      wrapperClassName={twMerge(
                        'transition-all duration-500 w-[200px] overflow-hidden h-9',
                      )}
                    />
                  </div>

                  <InfiniteScroll
                    className="relative flex h-full flex-col divide-y divide-gray-200 border-b-[1px] border-gray-200"
                    dataLength={stakeholders.length - 1}
                    hasMore={hasNextPage}
                    loader={
                      <div className="absolute h-full w-full animate-pulse bg-brand-300/10" />
                    }
                    next={fetchNextPage}
                    scrollableTarget="infinite-scroll-target"
                  >
                    {stakeholders.map(
                      ({ email, fullName, id }) =>
                        id !== userStakeholderId && (
                          <StakeholderRow
                            email={email}
                            fullName={fullName}
                            isSelected={id === stakeholderId}
                            key={id}
                            onClick={() => {
                              setStakeholderId(id);
                            }}
                          />
                        ),
                    )}
                  </InfiniteScroll>
                </div>
              )}

              <div className="h-full w-full overflow-y-auto">
                <div
                  className={twMerge(
                    'flex h-fit w-full flex-col gap-4 overflow-hidden rounded-lg p-1',
                    debounceSearch &&
                      !stakeholders.length &&
                      onStakeholderHoldings &&
                      'centered flex h-full',
                  )}
                >
                  <AnimatePresence mode="wait">
                    {debounceSearch && !stakeholders.length && onStakeholderHoldings ? (
                      <CrossfadePanel
                        className="centered flex h-full w-full"
                        key="holdings-empty"
                        panelKey="holdings-empty"
                      >
                        <EmptySearchList />
                      </CrossfadePanel>
                    ) : (
                      <CrossfadePanel
                        className="flex w-full flex-col gap-4"
                        key={`holdings-data-${screen}`}
                        panelKey={`holdings-data-${screen}`}
                      >
                        <div className="flex w-full flex-wrap gap-4">
                          <OwnershipSummary {...ownershipSummary} />
                          <ConsolidatedPosition shareClasses={consolidatedPosition} />
                        </div>
                        <div className="flex w-full flex-wrap gap-4">
                          {company?.stakeholder?.positionAccess && (
                            <PositionGraph snapshot={snapshot} stakeholderId={stakeholderId} />
                          )}

                          {company?.stakeholder?.capitalChangeAccess && (
                            <EquityGraph companyId={companyId} stakeholderId={stakeholderId} />
                          )}
                        </div>
                        <InvestmentLedger
                          queriesEnabled={holdingsQueriesEnabled}
                          stakeholderId={stakeholderId}
                        />
                        <AutomaticVesting
                          companyId={companyId}
                          disableExercise={onStakeholderHoldings}
                          queriesEnabled={holdingsQueriesEnabled}
                          stakeholderId={stakeholderId}
                        />
                        <SafeLedger stakeholderId={stakeholderId} />
                        <DividendsLedger
                          enabled={holdingsQueriesEnabled}
                          stakeholderId={stakeholderId}
                          stakeholderName={selectedStakeholderName}
                        />
                        <ReverseVesting
                          queriesEnabled={holdingsQueriesEnabled}
                          stakeholderId={stakeholderId}
                        />
                        <div className="flex w-full flex-col gap-3 rounded-lg bg-gray-50 p-1 pt-3">
                          <span className="pl-4 text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
                            <AppFormattedMessage id="INVESTMENT_SUMMARY" />
                          </span>
                          <div className="flex h-full gap-1 max-lg:flex-wrap">
                            {company?.stakeholder?.companyTableAccess && (
                              <div className="centered relative flex w-full">
                                {isEmptyCompanyInvestmentSummary && (
                                  <span className="absolute z-1 text-label-md font-medium text-gray-500">
                                    <AppFormattedMessage id="COMPANY_INVESTMENT_SUMMARY_WILL_BE_HERE" />
                                  </span>
                                )}
                                <div
                                  className={twMerge(
                                    'flex min-h-[342px] w-full flex-col divide-y divide-gray-100 rounded-md bg-white shadow-sm',
                                    isEmptyCompanyInvestmentSummary &&
                                      'select-none opacity-60 blur-md',
                                  )}
                                >
                                  <span className="p-4 text-xl font-semibold text-gray-700">
                                    <AppFormattedMessage id="COMPANY" />
                                  </span>
                                  <SummaryItem
                                    title={<AppFormattedMessage id="TOTAL_ROUNDS" />}
                                    value={formatDisplayValue(
                                      investmentSummary?.companyTotalRounds,
                                    )}
                                  />
                                  <SummaryItem
                                    title={<AppFormattedMessage id="CURRENT_VALUATION" />}
                                    value={formatDisplayValue(
                                      investmentSummary?.currentValuationValue,
                                      {
                                        prefix: currency?.symbol || currency?.iso3?.toUpperCase(),
                                      },
                                    )}
                                  />
                                  <SummaryItem
                                    title={<AppFormattedMessage id="TOTAL_RAISED_AMOUNT" />}
                                    value={formatDisplayValue(investmentSummary?.totalAmountRaise, {
                                      prefix: currency?.symbol || currency?.iso3?.toUpperCase(),
                                    })}
                                  />
                                  <SummaryItem
                                    title={<AppFormattedMessage id="AVG_VALUATION_CHANGE" />}
                                    titleAdditionalNote={<AppFormattedMessage id="PER_ROUND" />}
                                    value={formatDisplayValue(
                                      investmentSummary?.avgValuationChange,
                                      {
                                        multiply: 100,
                                        suffix: '%',
                                      },
                                    )}
                                  />
                                  <SummaryItem
                                    title={<AppFormattedMessage id="LATEST_SHARE_PRICE" />}
                                    value={formatDisplayValue(investmentSummary?.latestSharePrice)}
                                  />
                                </div>
                              </div>
                            )}
                            <div className="centered relative flex w-full">
                              {isEmptyYourParticipation && (
                                <span className="absolute z-1 text-label-md font-medium text-gray-500">
                                  <AppFormattedMessage id="YOUR_PARTICIPATION_SUMMARY_WILL_BE_HERE" />
                                </span>
                              )}
                              <div
                                className={twMerge(
                                  'flex w-full flex-col divide-y divide-gray-100 rounded-md bg-white shadow-sm',
                                  isEmptyYourParticipation && 'select-none opacity-60 blur-md',
                                )}
                              >
                                <span className="p-4 text-xl font-semibold text-gray-700">
                                  <AppFormattedMessage id="YOUR_PARTICIPATION" />
                                </span>
                                <SummaryItem
                                  title={<AppFormattedMessage id="TOTAL_ROUNDS" />}
                                  value={formatDisplayValue(
                                    investmentSummary?.stakeholderTotalRounds,
                                  )}
                                />
                                <SummaryItem
                                  title={<AppFormattedMessage id="TOTAL_INVESTMENT" />}
                                  value={formatDisplayValue(
                                    investmentSummary?.stakeholderTotalInvestment,
                                    { prefix: currency?.symbol || currency?.iso3?.toUpperCase() },
                                  )}
                                />
                                <SummaryItem
                                  title={<AppFormattedMessage id="ESTIMATED_VALUE" />}
                                  value={formatDisplayValue(
                                    investmentSummary?.stakeholderEstimatedValue,
                                    {
                                      prefix: currency?.symbol || currency?.iso3?.toUpperCase(),
                                    },
                                  )}
                                />
                                <SummaryItem
                                  title={<AppFormattedMessage id="OVERALL_EQUITY_CHANGE" />}
                                  titleAdditionalNote={<AppFormattedMessage id="TOTAL_ROUNDS" />}
                                  value={formatDisplayValue(investmentSummary?.equityChange, {
                                    multiply: 100,
                                    suffix: '%',
                                  })}
                                />
                                <SummaryItem
                                  title={<AppFormattedMessage id="AVERAGE_INVESTMENT" />}
                                  titleAdditionalNote={<AppFormattedMessage id="TOTAL_ROUNDS" />}
                                  value={formatDisplayValue(
                                    investmentSummary?.stakeholderAverageInvestment,
                                  )}
                                />
                                <SummaryItem
                                  title={<AppFormattedMessage id="AVERAGE_SHARE_PRICE" />}
                                  value={formatDisplayValue(investmentSummary?.averageSharePrice, {
                                    prefix: currency?.symbol || currency?.iso3?.toUpperCase(),
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </CrossfadePanel>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </CrossfadePanel>
        )}
      </AnimatePresence>
    </div>
  );
};

MyHoldings.displayName = 'MyHoldings';

export default MyHoldings;
